import { CourseMaterial, useCoursesLoaderData } from "./coursesLoader.ts";
import styles from "./MaterialAccessBlock.module.scss";
import { FormattedMessage, useIntl } from "react-intl";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import { clsx } from "clsx";
import useProgramDecision from "./useProgramDecision.tsx";
import useOptedIn from "./useOptedIn.tsx";
import useVirtualStore from "./useVirtualStore.tsx";
import useProgramName from "./useProgramName.tsx";
import Tagline from "./common/Tagline.tsx";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";

function IAOptedInBlock({
  material,
  onOptOut,
}: {
  material: CourseMaterial;
  onOptOut?: () => void;
}) {
  const intl = useIntl();
  const programName = useProgramName();
  const price = intl.formatNumber(material.studentPrice ?? 0, {
    style: "currency",
    currency: material.currency ?? "USD",
  });
  return (
    <div className={styles.container} data-testid="ia-block-in">
      <div className={styles.info}>
        <Tagline iconType={"greenCheck"}>
          <FormattedMessage defaultMessage={"Best Price + Instant Access"} />
        </Tagline>
        <div className={styles.message}>{programName}</div>
        <div className={styles.pricing}>
          <FormattedMessage
            defaultMessage={
              "<strong>{itemPrice}</strong>, billed to student account"
            }
            values={{
              itemPrice: price,
              strong: (content) => <strong>{content}</strong>,
            }}
          />
        </div>
      </div>
      {onOptOut && (
        <div className={styles.action}>
          <BaseLinkOrButton className="vst-link" onClick={() => onOptOut()}>
            <FormattedMessage defaultMessage={"Opt Out"} />
          </BaseLinkOrButton>
        </div>
      )}
    </div>
  );
}

function IAOptedOutBlock({ material }: { material: CourseMaterial }) {
  const intl = useIntl();
  const decision = useProgramDecision();
  const isNoProgram = decision === "no_program";
  const programName = useProgramName(
    isNoProgram ? "inclusive_access" : undefined
  );
  const { optDeadline } = useMaterialOptDeadline(material);

  // I checked, saving is based off highest store price to inflate the saving...
  const highestStorePrice = (
    material.product?.map((v) => v.variants ?? []).flat() ?? []
  ).reduce<number>((acc, cur) => {
    const curPrice = Number(cur.studentPrice);
    return isNaN(curPrice) || curPrice < acc ? acc : curPrice;
  }, 0);
  let saving = (highestStorePrice ?? 0) - (material?.studentPrice ?? 0);
  if (saving < 0) {
    // don't think should happen for real, but call it out with a zero
    // save -$12.00 (i.e. you are getting ripped off) might be interpreted as a discount
    // the other option is "pay $12.00 more by opting in" which seems ridiculous
    saving = 0;
  }

  const savingAmount = intl.formatNumber(saving, {
    style: "currency",
    currency: material.currency ?? "USD",
  });

  return (
    <div
      className={clsx(styles.container, styles.optedOut)}
      data-testid="ia-block-out"
    >
      <div className={styles.info}>
        <div className={styles.message}>
          <FormattedMessage
            defaultMessage={"Opted out of {programName}"}
            values={{ programName }}
          />
        </div>
        <div className={styles.pricing}>
          <div>
            <FormattedMessage
              defaultMessage={
                "Save {savingAmount} by opting back into {programName}."
              }
              values={{
                programName,
                savingAmount,
              }}
            />
          </div>
          {optDeadline && (
            <FormattedMessage
              tagName="div"
              defaultMessage="Opt back in until {date, date, ::yyMMdd}."
              values={{
                date: new Date(optDeadline),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function EAOptedInBlock() {
  const programName = useProgramName();
  return (
    <div className={styles.container} data-testid="ea-block-in">
      <div className={styles.info}>
        <div className={styles.message}>{programName}</div>
        <div className={styles.pricing}>
          <FormattedMessage
            defaultMessage={"Included in program; billed to student account"}
          />
        </div>
      </div>
    </div>
  );
}

export default function MaterialAccessBlock({
  material,
  onOptOut,
  onOptIn,
}: {
  material: CourseMaterial;
  onOptOut?: () => void;
  onOptIn?: () => void;
}) {
  const { currentTerm } = useCoursesLoaderData();
  const decision = useProgramDecision();
  const optedIn = useOptedIn(material);
  const { isVirtualStore } = useVirtualStore();
  const { optWindowOpen } = useMaterialOptDeadline(material);

  if (!isVirtualStore || (!onOptOut && !onOptIn)) {
    return null;
  }

  if (material.optOut && !material.product) {
    // not in store scenario so can't compute saving so better not to show it
    // "more purchase options" won't be available either, but user can recover with "Opt In"
    return null;
  }

  if (
    !optWindowOpen ||
    (currentTerm?.institutionProgram === "equitable_access" && optedIn)
  ) {
    // EA opt out is at the term level (with a message in the material block to call that out)
    return null;
  }

  if (decision === "inclusive_access" || decision === "no_program") {
    return optedIn ? (
      <IAOptedInBlock material={material} onOptOut={onOptOut} />
    ) : (
      <IAOptedOutBlock material={material} />
    );
  } else if (decision === "equitable_access" && optedIn) {
    return <EAOptedInBlock />;
  }

  return null;
}
